import { InlineEditableField } from '@components'
import { VatCodeIcons } from '@root/components/Inputs/VatSelect/VatSelect'
import { VatCode, getContactPageUrl } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import { IAccountingSuggestion } from '@root/query/suggestions'
import { findOptionByValue, formatCurrency } from '@utils'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaAlignLeft, FaAddressCard, FaBook, FaTags, FaMagic, FaPercentage } from 'react-icons/fa'
import { FaCircle } from 'react-icons/fa6'
import { SuggestionCardContactSelector } from './SuggestionCardContactSelector'
import {
  AccountAmount,
  AccountLabel,
  AccountRow,
  AccountTagLeadIcon,
  AccountTagPill,
  AccountsEyebrowRow,
  BadgeRow,
  ContactAccountsSection,
  ContactHeaderRow,
  ContactInlineSuggestionBlock,
  ContactInlineSuggestionDisplayName,
  ContactInlineSuggestionIcon,
  ContactInlineSuggestionLabel,
  ContactInlineText,
  ContactInlineValue,
  ContactLink,
  ContactNameText,
  DescriptionInput,
  DescriptionText,
  DescriptionValue,
  Eyebrow,
  GutterIcon,
  GutterSection,
  InlineFieldErrorText,
  MutedText,
  AttentionDot,
  ReviewHint,
  ReviewHintSlot,
  SectionContent,
  SuggestionContentDivider,
  UIRowItem,
  UIRowsContainer,
  UncertainAccountName,
  UncertaintyHoverAnchor
} from '../styles'
import { SuggestionCardFormValues } from '../types'
import { UncertaintyHoverCard } from '../uncertainty/UncertaintyHoverCard'
import { useSuggestionUncertainty } from '../uncertainty/SuggestionUncertaintyContext'

interface SuggestionCardDetailsSectionProps {
  suggestion: IAccountingSuggestion
  onUseSuggestedContact: () => void
}

export const SuggestionCardDetailsSection: React.FC<SuggestionCardDetailsSectionProps> = ({
  suggestion,
  onUseSuggestedContact
}) => {
  const hasEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const { slug, data: business } = useBusinessContext()
  const { data: vatCodeOptions } = useVATCodes()
  const { t } = useTranslation()
  const { control, clearErrors, formState } = useFormContext<SuggestionCardFormValues>()
  const hasDescriptionError = Boolean(formState.errors.description)
  const descriptionErrorMessage = formState.errors.description?.message
  const contactErrorMessage = formState.errors.contact?.message
  const hasContactSuggestion = Boolean(suggestion.contact_name)
  const getShortVatCodeText = (vatCode: VatCode) =>
    findOptionByValue(vatCodeOptions, vatCode)?.label_short

  const uncertainty = suggestion.uncertainty ?? []
  const { enabled: showV3Design, highlight, isOpen } = useSuggestionUncertainty()
  const accountUncertain = isOpen('account_unreliable')
  const vatUncertain = isOpen('vat_unreliable')
  const splitUncertain = isOpen('split_uncertain')
  const settlementAccountNumber = suggestion.source_account?.number
  const supplierName =
    suggestion.contact_name || t('document.suggestionCard.review.supplierFallback')
  const bookedAmount = formatCurrency(suggestion.amount)

  return (
    <>
      <SuggestionContentDivider />

      {uncertainty.length > 0 && !showV3Design && (
        <GutterSection>
          <GutterIcon>
            <FaMagic />
          </GutterIcon>
          <SectionContent>
            <Eyebrow>{t('document.suggestionCard.uncertaintyTitle')}</Eyebrow>
            {uncertainty.map(code => (
              <MutedText key={code}>{t(`document.suggestionCard.uncertainty.${code}`)}</MutedText>
            ))}
          </SectionContent>
        </GutterSection>
      )}

      <GutterSection>
        <GutterIcon>
          <FaAlignLeft />
        </GutterIcon>
        <SectionContent>
          <Eyebrow>{t('document.description')}</Eyebrow>
          <Controller
            name="description"
            control={control}
            rules={{
              required: { value: true, message: t('validation.required') },
              validate: (value: string | undefined) =>
                value && value.trim().length > 0 ? true : t('validation.required')
            }}
            render={({ field }) => (
              <InlineEditableField
                value={field.value || ''}
                onChange={value => {
                  field.onChange(value ?? '')
                  if (hasDescriptionError) clearErrors('description')
                }}
                editable={hasEditPermission}
                minWidth={150}
                title={t('document.suggestionCard.editDescription')}
                state={hasDescriptionError ? 'error' : 'default'}
                ValueComponent={DescriptionValue}
                InputComponent={DescriptionInput}
                TextComponent={DescriptionText}
              />
            )}
          />
          {descriptionErrorMessage && (
            <InlineFieldErrorText>{String(descriptionErrorMessage)}</InlineFieldErrorText>
          )}
        </SectionContent>
      </GutterSection>

      <ContactAccountsSection>
        <GutterSection>
          <GutterIcon>
            <FaAddressCard />
          </GutterIcon>
          <SectionContent>
            <ContactHeaderRow>
              <Eyebrow>{t('document.contact')}</Eyebrow>
            </ContactHeaderRow>
            {hasEditPermission ? (
              <Controller
                name="contact"
                control={control}
                rules={{
                  required: !hasContactSuggestion
                    ? { value: true, message: t('validation.required') }
                    : undefined
                }}
                render={({ field }) => (
                  <SuggestionCardContactSelector
                    value={field.value}
                    onChange={nextContactId => {
                      clearErrors('contact')
                      field.onChange(nextContactId)
                    }}
                    fallbackContactName={suggestion.contact_name}
                    showPendingNewContactCaption={
                      field.value == null && suggestion.contact == null && hasContactSuggestion
                    }
                    pendingNewContactLabel={t('contacts.contactSelector.newContact')}
                    showUseSuggestedContactAction={
                      typeof field.value === 'number' &&
                      suggestion.contact != null &&
                      field.value !== suggestion.contact
                    }
                    useSuggestedContactLabel={t('document.suggestionCard.useSuggestedContact')}
                    onUseSuggestedContact={onUseSuggestedContact}
                    title={t('document.contactInfo')}
                    ValueComponent={ContactInlineValue}
                    TextComponent={ContactInlineText}
                  />
                )}
              />
            ) : suggestion.contact ? (
              <ContactNameText>
                <ContactLink to={getContactPageUrl(slug, { id: suggestion.contact })}>
                  {suggestion.contact_name}
                </ContactLink>
              </ContactNameText>
            ) : suggestion.contact_name ? (
              <ContactInlineSuggestionBlock>
                <ContactInlineSuggestionLabel>
                  <ContactInlineSuggestionIcon>
                    <FaMagic aria-hidden />
                  </ContactInlineSuggestionIcon>
                  <span>{t('contacts.contactSelector.newContact')}</span>
                </ContactInlineSuggestionLabel>
                <ContactInlineSuggestionDisplayName>
                  {suggestion.contact_name}
                </ContactInlineSuggestionDisplayName>
              </ContactInlineSuggestionBlock>
            ) : (
              <MutedText>-</MutedText>
            )}
            {contactErrorMessage && (
              <InlineFieldErrorText>{String(contactErrorMessage)}</InlineFieldErrorText>
            )}
          </SectionContent>
        </GutterSection>

        <GutterSection
          id={`suggestion-${suggestion.id}-accounts`}
          $highlight={highlight === 'accounts'}
        >
          <GutterIcon>
            <FaBook />
          </GutterIcon>
          <SectionContent>
            <AccountsEyebrowRow>
              <Eyebrow>{t('document.suggestionCard.sectionAccounts')}</Eyebrow>
              {splitUncertain && (
                <ReviewHintSlot $align="end">
                  <ReviewHint>
                    <AttentionDot />
                    {t('document.suggestionCard.review.checkSplit')}
                  </ReviewHint>
                  <UncertaintyHoverCard
                    code="split_uncertain"
                    align="end"
                    promptContext={{ amount: bookedAmount, contact: supplierName }}
                  />
                </ReviewHintSlot>
              )}
            </AccountsEyebrowRow>
            <UIRowsContainer>
              {suggestion.ui_rows.map((row, index) => {
                const vatCode = row.vat_code ?? VatCode.TAX_FREE
                const VatCodeIcon = VatCodeIcons[vatCode] ?? FaCircle
                const isSettlementRow =
                  settlementAccountNumber != null &&
                  String(row.account_number) === String(settlementAccountNumber)
                const markAccount = accountUncertain && !isSettlementRow
                const markVat = vatUncertain && !isSettlementRow
                return (
                  <UIRowItem
                    key={`${row.account_number}-${row.account_name}-${row.amount}-${index}`}
                  >
                    <AccountRow>
                      {markAccount ? (
                        <UncertaintyHoverAnchor>
                          <UncertainAccountName>
                            {row.account_number} {row.account_name}
                            <AttentionDot />
                          </UncertainAccountName>
                          <UncertaintyHoverCard
                            code="account_unreliable"
                            promptContext={{
                              account: `${row.account_number} ${row.account_name}`,
                              contact: supplierName,
                              amount: formatCurrency(row.amount)
                            }}
                          />
                        </UncertaintyHoverAnchor>
                      ) : (
                        <AccountLabel>
                          {row.account_number} {row.account_name}
                        </AccountLabel>
                      )}
                      <AccountAmount>{formatCurrency(row.amount)}</AccountAmount>
                    </AccountRow>
                    <UncertaintyHoverAnchor>
                      <BadgeRow>
                        <AccountTagPill $uncertain={markVat}>
                          <AccountTagLeadIcon $tone="info" aria-hidden>
                            {markVat ? <AttentionDot /> : <VatCodeIcon />}
                          </AccountTagLeadIcon>
                          {getShortVatCodeText(vatCode)}
                        </AccountTagPill>
                        {row.vat_code && row.vat_code !== VatCode.TAX_FREE && (
                          <AccountTagPill $uncertain={markVat}>
                            <AccountTagLeadIcon $tone={markVat ? 'review' : 'success'} aria-hidden>
                              {markVat ? <AttentionDot /> : <FaPercentage />}
                            </AccountTagLeadIcon>
                            {t('general.vat', { percentage: row.vat_rate })}
                          </AccountTagPill>
                        )}
                      </BadgeRow>
                      {markVat && (
                        <UncertaintyHoverCard
                          code="vat_unreliable"
                          promptContext={{ contact: supplierName, amount: bookedAmount }}
                        />
                      )}
                    </UncertaintyHoverAnchor>
                  </UIRowItem>
                )
              })}
            </UIRowsContainer>
          </SectionContent>
        </GutterSection>

        {business?.has_tags && (
          <GutterSection>
            <GutterIcon>
              <FaTags />
            </GutterIcon>
            <SectionContent>
              <Eyebrow>{t('document.tags')}</Eyebrow>
              <Controller
                name="tag_ids"
                control={control}
                render={({ field }) => <TagSelector {...field} isReadOnly={!hasEditPermission} />}
              />
            </SectionContent>
          </GutterSection>
        )}
      </ContactAccountsSection>
    </>
  )
}
